/**
 * Profile page component.
 */

import { useAuth } from '@/contexts/AuthContext';
import { apiClient, AccessRightsResponse, AccessPermission } from '@/services/apiClient';
import { formatDate } from '@/utils/helpers';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import toast from 'react-hot-toast';

function maskPhoneNumber(phone: string | null | undefined, t: (key: string) => string): string {
  if (!phone) return t('profile.notSet');
  if (phone.length <= 4) return phone;
  return `***-***-${phone.slice(-4)}`;
}

interface Role {
  name?: string;
  description?: string;
}

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [phoneNumber, setPhoneNumber] = useState(user?.phone_number || '');
  const [isUpdating, setIsUpdating] = useState(false);

  const { data: rolesData } = useQuery<Role[]>({
    queryKey: ['userRoles'],
    queryFn: async () => {
      const response = await apiClient.getUserRoles();
      if (response.error) {
        throw new Error(response.error);
      }
      return (response.data as Role[]) || [];
    },
  });

  // Access rights query
  const {
    data: accessRights,
    isLoading: isLoadingAccess,
    error: accessError,
  } = useQuery<AccessRightsResponse>({
    queryKey: ['accessRights'],
    queryFn: async () => {
      const response = await apiClient.getMyAccessRights();
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as AccessRightsResponse;
    },
  });

  // Refresh access mutation
  const refreshAccessMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.refreshAccessRights();
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['accessRights'] });
      toast.success(data?.message || 'Access rights refreshed successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to refresh access: ${error.message}`);
    },
  });

  const handleUpdateProfile = async () => {
    setIsUpdating(true);
    try {
      const response = await apiClient.updateUserProfile({ phone_number: phoneNumber });
      if (response.error) {
        toast.error(response.error);
        return;
      }
      toast.success(t('profile.profileUpdated'));
      await refreshUser();
    } catch (error) {
      toast.error(t('profile.failedToUpdate'));
    } finally {
      setIsUpdating(false);
    }
  };

  if (!user) {
    return <Alert variant="info">{t('profile.pleaseLogin')}</Alert>;
  }

  return (
    <div>
      <h2 className="mb-4" style={{ fontWeight: 600, color: 'var(--myant-text-primary)' }}>
        {t('profile.title')}
      </h2>
      <Row>
        <Col md={8}>
          <Card className="mb-4">
            <Card.Header>{t('profile.basicInfo')}</Card.Header>
            <Card.Body>
              <p>
                <strong>{t('profile.email')}:</strong> {user.email || 'N/A'}
              </p>
              <p>
                <strong>{t('profile.phone')}:</strong> {maskPhoneNumber(user.phone_number, t)}{' '}
                {user.phone_verified ? (
                  <span className="text-success">({t('profile.verified')})</span>
                ) : (
                  <span className="text-warning">({t('profile.notVerified')})</span>
                )}
              </p>
              <p>
                <strong>{t('profile.accountStatus')}:</strong>{' '}
                <span className={user.is_active !== false ? 'text-success' : 'text-danger'}>
                  {user.is_active !== false ? t('profile.active') : t('profile.inactive')}
                </span>
              </p>
            </Card.Body>
          </Card>

          {user.google_name && (
            <Card className="mb-4">
              <Card.Header>{t('profile.googleAccountInfo')}</Card.Header>
              <Card.Body>
                <p>
                  <strong>{t('profile.googleName')}:</strong> {user.google_name}
                </p>
                {user.google_id ? (
                  <p>
                    <strong>{t('profile.googleId')}:</strong> {String(user.google_id)}
                  </p>
                ) : null}
              </Card.Body>
            </Card>
          )}

          <Card className="mb-4">
            <Card.Header>{t('profile.accountDetails')}</Card.Header>
            <Card.Body>
              {user.created_at ? (
                <p>
                  <strong>{t('profile.accountCreated')}:</strong> {formatDate(String(user.created_at))}
                </p>
              ) : null}
              {user.updated_at ? (
                <p>
                  <strong>{t('profile.lastUpdated')}:</strong> {formatDate(String(user.updated_at))}
                </p>
              ) : null}
              {user.last_login ? (
                <p>
                  <strong>{t('profile.lastLogin')}:</strong> {formatDate(String(user.last_login))}
                </p>
              ) : null}
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>{t('profile.editProfile')}</Card.Header>
            <Card.Body>
              <p>{t('profile.updateInfo')}</p>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('profile.phoneNumber')}</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder={t('profile.phonePlaceholder')}
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                    <Form.Text className="text-muted">{t('profile.keepCurrentNumber')}</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('profile.email')}</Form.Label>
                    <Form.Control type="email" value={user.email || ''} disabled />
                  </Form.Group>
                </Col>
              </Row>
              <Button variant="primary" onClick={handleUpdateProfile} disabled={isUpdating} className="me-2">
                {t('profile.updateProfile')}
              </Button>
            </Card.Body>
          </Card>

          {rolesData && (
            <Card className="mb-4">
              <Card.Header>{t('profile.yourRoles')}</Card.Header>
              <Card.Body>
                <p>
                  <strong>{t('profile.totalRoles')}:</strong> {Array.isArray(rolesData) ? rolesData.length : 0} {t('profile.role')}
                </p>
                {Array.isArray(rolesData) && rolesData.length > 0 ? (
                  <ul>
                    {rolesData.map((role, index) => (
                      <li key={index}>
                        <strong>{role.name || 'Unknown Role'}:</strong> {role.description || 'No description'}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted">{t('profile.noRoles')}</p>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Access Rights Section */}
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Data Access Rights</span>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => refreshAccessMutation.mutate()}
                disabled={refreshAccessMutation.isPending}
              >
                {refreshAccessMutation.isPending ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Refreshing...
                  </>
                ) : (
                  <>🔄 Refresh Access</>
                )}
              </Button>
            </Card.Header>
            <Card.Body>
              {isLoadingAccess ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" /> Loading access rights...
                </div>
              ) : accessError ? (
                <Alert variant="warning">
                  Unable to load access rights. Click Refresh to try again.
                </Alert>
              ) : accessRights?.permissions && accessRights.permissions.length > 0 ? (
                <>
                  <p className="text-muted mb-2">
                    <small>
                      Last calculated:{' '}
                      {accessRights.calculated_at
                        ? formatDate(accessRights.calculated_at)
                        : 'Not yet calculated'}
                    </small>
                  </p>
                  <p>
                    <strong>Total Permissions:</strong>{' '}
                    <Badge bg="info">{accessRights.permissions.length}</Badge> device access grants
                  </p>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <Table size="sm" striped hover>
                      <thead>
                        <tr>
                          <th>Device ID</th>
                          <th>Time Window</th>
                          <th>Data Types</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accessRights.permissions.slice(0, 20).map((perm: AccessPermission, idx: number) => (
                          <tr key={idx}>
                            <td>
                              <code>{perm.device_id}</code>
                            </td>
                            <td>
                              <small>
                                {perm.time_window.start
                                  ? new Date(perm.time_window.start).toLocaleDateString()
                                  : '∞'}{' '}
                                →{' '}
                                {perm.time_window.end
                                  ? new Date(perm.time_window.end).toLocaleDateString()
                                  : '∞'}
                              </small>
                            </td>
                            <td>
                              {perm.data_types.map((dt) => (
                                <Badge key={dt} bg="secondary" className="me-1">
                                  {dt}
                                </Badge>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {accessRights.permissions.length > 20 && (
                      <p className="text-muted text-center">
                        <small>
                          Showing 20 of {accessRights.permissions.length} permissions
                        </small>
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <Alert variant="info">
                  No data access permissions found. Contact your administrator to request access.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
